import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { AdminBlogAuthorEditorFields } from './AdminBlogAuthorInvitePage';
import AdminPersonLedgerSection from './AdminPersonLedgerSection';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  buildYowanPersonOptions,
  ensureYowanAdminMemberMembership,
  fetchYowanAdminBlogAuthorAccessDetail,
  fetchYowanAdminPersonDetail,
  fetchYowanAdminPersonList,
  getYowanAdminMutationErrorMessage,
  resolveYowanAdminPersonId,
  updateYowanAdminBlogAuthorAccess,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const preferFilledPersonValue = (primary, fallback) => {
  const value = String(primary || '').trim();

  return value ? primary : fallback || '';
};

const mergePersonLedgerRecord = (partialPerson = null, fullPerson = null) => {
  if (!partialPerson) {
    return fullPerson || null;
  }

  if (!fullPerson) {
    return partialPerson;
  }

  return {
    ...partialPerson,
    ...fullPerson,
    id: fullPerson.id || partialPerson.id,
    displayName: preferFilledPersonValue(fullPerson.displayName, partialPerson.displayName),
    nameKana: preferFilledPersonValue(fullPerson.nameKana, partialPerson.nameKana),
    email: preferFilledPersonValue(fullPerson.email, partialPerson.email),
    phone: preferFilledPersonValue(fullPerson.phone, partialPerson.phone),
    organization: preferFilledPersonValue(fullPerson.organization, partialPerson.organization),
    roleTitle: preferFilledPersonValue(fullPerson.roleTitle, partialPerson.roleTitle),
    internalNote: preferFilledPersonValue(fullPerson.internalNote, partialPerson.internalNote),
    isActive: fullPerson.isActive !== false && partialPerson.isActive !== false,
  };
};

const resolveRecordPersonId = (record = {}) =>
  record.personId ||
  record.person?.id ||
  record.author?.personId ||
  record.author?.person?.id ||
  '';

const applyPersonLedgerValues = (setValue, person = null) => {
  if (!setValue || !person) {
    return;
  }

  setValue('personId', person.id || '');
  setValue('personDisplayName', person.displayName || '');
  setValue('personNameKana', person.nameKana || '');
  setValue('personEmail', person.email || '');
  setValue('personPhone', person.phone || '');
  setValue('personOrganization', person.organization || '');
  setValue('personRoleTitle', person.roleTitle || '');
  setValue('personInternalNote', person.internalNote || '');
  setValue('personIsActive', person.isActive !== false);
};

const hasSamePersonLedgerValues = (left = null, right = null) => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return [
    'id',
    'displayName',
    'nameKana',
    'email',
    'phone',
    'organization',
    'roleTitle',
    'internalNote',
  ].every((key) => String(left[key] || '') === String(right[key] || '')) &&
    (left.isActive !== false) === (right.isActive !== false);
};

const getPersonLedgerDefaults = (record = {}) => {
  const person = record.person || record.author?.person || null;

  return {
    personId: resolveRecordPersonId(record),
    personDisplayName: person?.displayName || '',
    personNameKana: person?.nameKana || '',
    personEmail: person?.email || '',
    personPhone: person?.phone || '',
    personOrganization: person?.organization || '',
    personRoleTitle: person?.roleTitle || '',
    personInternalNote: person?.internalNote || '',
    personIsActive: person?.isActive !== false,
  };
};

const AdminBlogAuthorEditPage = () => {
  const content = yowanPageContent['/admin/content/blog-authors/:accessId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { accessId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [personOptions, setPersonOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPersonOptions, setIsLoadingPersonOptions] = useState(false);
  const [personOptionLoadError, setPersonOptionLoadError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [membershipMessage, setMembershipMessage] = useState('');
  const [isMembershipSubmitting, setIsMembershipSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
  });

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanAdminBlogAuthorAccessDetail(accessId)
      .then(async (record) => {
        if (!isMounted) {
          return;
        }

        let fullPerson = null;

        const recordPersonId = resolveRecordPersonId(record);

        if (recordPersonId) {
          try {
            fullPerson = await fetchYowanAdminPersonDetail(recordPersonId);
          } catch (personError) {
            fullPerson = null;
          }
        }

        if (!isMounted) {
          return;
        }

        const hydratedRecord = {
          ...record,
          personId: recordPersonId || fullPerson?.id || '',
          person: mergePersonLedgerRecord(record.person, fullPerson),
        };

        setCurrentRecord(hydratedRecord);
        reset({
          email: hydratedRecord.email || '',
          authorName: hydratedRecord.authorName || '',
          profileUrl: hydratedRecord.profileUrl || '',
          canPublishBlog: hydratedRecord.canPublishBlog,
          isActive: hydratedRecord.isActive,
          sendInvite: false,
          ...getPersonLedgerDefaults(hydratedRecord),
        });
      })
      .catch(() => {
        if (isMounted) {
          setCurrentRecord(null);
          setSubmitMessage(content?.form.loadErrorMessage || 'ブログ執筆者を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [accessId, content?.form.loadErrorMessage, reset]);

  useEffect(() => {
    if (!content || !currentRecord) {
      return undefined;
    }

    let isMounted = true;

    const loadPersonOptions = async () => {
      setIsLoadingPersonOptions(true);
      setPersonOptionLoadError('');

      try {
        const people = await fetchYowanAdminPersonList();

        if (isMounted) {
          const personId = resolveRecordPersonId(currentRecord);
          const matchedPerson = personId
            ? people.results.find((person) => person.id === personId) || null
            : null;
          const hydratedPerson = mergePersonLedgerRecord(currentRecord.person, matchedPerson);
          const hydratedRecord = hydratedPerson
            ? {
                ...currentRecord,
                personId: personId || hydratedPerson.id || '',
                person: hydratedPerson,
              }
            : currentRecord;

          if (!hasSamePersonLedgerValues(currentRecord.person, hydratedRecord.person)) {
            setCurrentRecord(hydratedRecord);
          }
          setPersonOptions(buildYowanPersonOptions(people.results, hydratedRecord.person));

          if (hydratedPerson && (watch?.('personId') || personId) === hydratedPerson.id) {
            applyPersonLedgerValues(setValue, hydratedPerson);
          }
        }
      } catch (error) {
        if (isMounted) {
          setPersonOptionLoadError('人物台帳の選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPersonOptions(false);
        }
      }
    };

    loadPersonOptions();

    return () => {
      isMounted = false;
    };
  }, [content, currentRecord, setValue, watch]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const personId = await resolveYowanAdminPersonId(values, currentRecord?.person);

      await updateYowanAdminBlogAuthorAccess(accessId, { ...values, personId });
      navigate(appendYowanPreviewQueryIfNeeded('/admin/content/blog-authors', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnsureMembership = async () => {
    const authSubjectId = currentRecord?.authSubjectId || currentRecord?.authSubject?.id || '';

    if (!authSubjectId) {
      setMembershipMessage(content.form.membershipAccess?.authRequiredText || '先に認証主体を作成してください。');
      return;
    }

    setIsMembershipSubmitting(true);
    setMembershipMessage(content.form.membershipAccess?.pendingMessage || '会員資格を付与しています。');

    try {
      const result = await ensureYowanAdminMemberMembership({
        authSubjectId,
        name: currentRecord?.authorName || currentRecord?.author?.displayName || '',
        email: currentRecord?.email || currentRecord?.authSubject?.loginEmail || '',
        sendPasswordSetup: true,
      });

      setCurrentRecord((current) => current ? {
        ...current,
        member: result.member || current.member,
        memberAccess: result.memberAccess || current.memberAccess,
        canEnsureMembership: false,
      } : current);
      setMembershipMessage(content.form.membershipAccess?.successMessage || '会員資格を付与しました。');
    } catch (error) {
      setMembershipMessage(getYowanAdminMutationErrorMessage(error, content.form.membershipAccess?.errorMessage || '会員資格を付与できませんでした。'));
    } finally {
      setIsMembershipSubmitting(false);
    }
  };

  const membershipContent = content.form.membershipAccess || {};
  const loginSetupContent = content.form.loginSetup || {};
  const membershipAuthSubjectId = currentRecord?.authSubjectId || currentRecord?.authSubject?.id || '';
  const hasMemberProfile = Boolean(currentRecord?.member?.id);
  const hasActiveMemberAccess = currentRecord?.memberAccess?.isActive === true;
  const canEnsureMembership = currentRecord?.canEnsureMembership === true;
  const onboarding = currentRecord?.onboarding || {};
  const latestRequestLabel = onboarding.latestRequestId
    ? onboarding.latestRequestId
    : loginSetupContent.latestRequestEmptyText || '依頼なし';
  const latestStatusLabel = onboarding.latestRequestStatus
    ? onboarding.latestRequestStatus
    : loginSetupContent.unknownStatusText || '未取得';

  return (
    <AdminBlogShell activeNavHref="/admin/content/blog-authors" routeKey="/admin/content/blog-authors/:accessId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/content/blog-authors', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      {isLoading || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner /> : <p className="yowan-admin-form__message">{submitMessage || content.form.loadErrorMessage}</p>}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          <AdminBlogAuthorEditorFields
            content={content}
            register={register}
            errors={errors}
            emailReadOnly
          />
          <AdminPersonLedgerSection
            content={content}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            personOptions={personOptions}
            currentPerson={currentRecord?.person}
            isLoadingOptions={isLoadingPersonOptions}
            optionLoadError={personOptionLoadError}
          />

          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">{loginSetupContent.title || 'ログイン設定'}</h2>
              <p className="yowan-admin-detail-panel__lead">
                {loginSetupContent.lead || 'ブログ執筆者の初回パスワード設定状況を確認します。'}
              </p>
            </header>
            <div className="yowan-admin-event-detail__grid">
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{loginSetupContent.authLabel || '認証主体'}</span>
                <input className="c-form__control" readOnly type="text" value={membershipAuthSubjectId ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{loginSetupContent.passwordLabel || 'パスワード設定'}</span>
                <input
                  className="c-form__control"
                  readOnly
                  type="text"
                  value={onboarding.passwordSetupRequired ? loginSetupContent.passwordRequiredText || '未設定' : loginSetupContent.passwordReadyText || '設定済み'}
                />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{loginSetupContent.latestRequestLabel || '最新依頼'}</span>
                <input className="c-form__control" readOnly type="text" value={latestRequestLabel} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{loginSetupContent.latestStatusLabel || '最新依頼ステータス'}</span>
                <input className="c-form__control" readOnly type="text" value={latestStatusLabel} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{loginSetupContent.expiresAtLabel || '有効期限'}</span>
                <input className="c-form__control" readOnly type="text" value={onboarding.latestRequestExpiresAt || '-'} />
              </label>
            </div>
          </section>

          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">{membershipContent.title || '会員資格'}</h2>
              <p className="yowan-admin-detail-panel__lead">
                {membershipContent.lead || 'ブログ執筆者が会員でもある場合に、同じログインアカウントへ会員プロフィールと会員資格を付与します。'}
              </p>
            </header>
            <div className="yowan-admin-event-detail__grid">
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.authLabel || '認証主体'}</span>
                <input className="c-form__control" readOnly type="text" value={membershipAuthSubjectId ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.memberLabel || '会員プロフィール'}</span>
                <input className="c-form__control" readOnly type="text" value={hasMemberProfile ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.memberAccessLabel || '会員資格'}</span>
                <input
                  className="c-form__control"
                  readOnly
                  type="text"
                  value={hasActiveMemberAccess ? '有効' : currentRecord?.memberAccess?.id ? '停止中' : '未付与'}
                />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.emailLabel || '対象メールアドレス'}</span>
                <input className="c-form__control" readOnly type="text" value={currentRecord?.email || currentRecord?.authSubject?.loginEmail || '-'} />
              </label>
            </div>
            {hasActiveMemberAccess && !canEnsureMembership ? (
              <p className="yowan-admin-form__field-note">{membershipContent.readyText || '会員プロフィールは作成済みです。'}</p>
            ) : canEnsureMembership && membershipAuthSubjectId ? (
              <div className="yowan-admin-form__actions">
                <button type="button" className="c-form__submit" disabled={isMembershipSubmitting} onClick={handleEnsureMembership}>
                  {membershipContent.actionLabel || '会員資格を付与'}
                </button>
              </div>
            ) : membershipAuthSubjectId ? (
              <p className="yowan-admin-form__field-note">{membershipContent.unavailableText || '会員資格を付与できる状態ではありません。対象アカウントの状態を確認してください。'}</p>
            ) : (
              <p className="yowan-admin-form__field-note">{membershipContent.authRequiredText || '先に認証主体を作成してください。'}</p>
            )}
            {isMembershipSubmitting ? (
              <p className="yowan-admin-form__message yowan-admin-form__message--pending">
                <LoadingSpinner inline size="sm" />
                <span>{membershipMessage}</span>
              </p>
            ) : membershipMessage ? (
              <p className="yowan-admin-form__message">{membershipMessage}</p>
            ) : null}
          </section>

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {content.form.submitLabel}
            </button>
          </div>
          {isSubmitting ? (
            <p className="yowan-admin-form__message yowan-admin-form__message--pending">
              <LoadingSpinner inline size="sm" />
              <span>{content.form.pendingMessage}</span>
            </p>
          ) : submitMessage ? (
            <p className="yowan-admin-form__message">{submitMessage}</p>
          ) : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminBlogAuthorEditPage;
